/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { axiosInstance, axiosInstanceV2, axiosInstanceV3 } from '../axios';
import { IAgreement } from '../dashboard/types';
import { IActivity, IModulesList, INotification, IPromotion, RequestParameters } from '../types';
import {
  getNonPaginatedEndpoint,
  getNonPaginatedEndpointProd,
  getPaginatedEndpoint,
  snakeCaseDictToCamelCaseDict,
  urlWithParams,
} from '../utils';
import { IContractResponse, IFaturasResponse, IHistory, IPromotionDetails, ISale, ISales } from './types';

export interface IAddress {
  street: string;
  country: string;
  city: string;
  state: string;
  neighborhood: string;
  cep: string;
  formattedAddress: string;
}

export const getState = () => {
  return axiosInstance.get('/states/').then(({ data }) => data);
};

export const getCity = (state = '') => {
  return axiosInstance.get(`/cities/?state=${state}`).then(({ data }) => data);
};

export function getLandingProd(slug: string, type: string = 'club'): Promise<IAgreement> {
  return getNonPaginatedEndpointProd(`/landings/?slug=${slug}&type=${type}`) as Promise<IAgreement>;
}

export function getLanding(slug: string, type: string = 'club'): Promise<IAgreement> {
  return getNonPaginatedEndpoint(`/landings/?slug=${slug}&type=${type}`) as Promise<IAgreement>;
}

export function getBenefits(params?: RequestParameters): Promise<IModulesList[]> {
  return axiosInstanceV2
    .get(`/agreement-modules/`, {
      params,
    })
    .then(({ data }) => data);
}

export function getNotifications(params?: RequestParameters): Promise<INotification[]> {
  const paramsAux: any = {};
  Object.entries(params || {}).reduce((acc: any, [key, value]) => {
    if (value) {
      paramsAux[key] = value;
    }
  }, {});

  return getNonPaginatedEndpoint(`/notifications`, paramsAux, axiosInstanceV2) as Promise<INotification[]>;
}

export function getFaturas(): Promise<IFaturasResponse> {
  return axiosInstance.get(`/faturas/`).then(({ data }) => data);
}

export function getContract(): Promise<IContractResponse> {
  return axiosInstance.get(`/contratos/`).then(({ data }) => data);
}

export async function getPromotions(params?: RequestParameters): Promise<IPromotion[]> {
  const paramsAux: any = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value) {
      paramsAux[key] = value;
    }
  });
  const { results } = await getPaginatedEndpoint(`/promotion/promotion-list/`, paramsAux, axiosInstanceV3);
  return results as IPromotion[];
}

export function getPromotionDetails(id: string): Promise<IPromotionDetails> {
  return axiosInstanceV2
    .get(`/promotion/promotions/${id}/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IPromotionDetails);
}

export function getDiscountDetails(id: string): Promise<IPromotionDetails> {
  return axiosInstance
    .get(`/discounts/${id}/`)
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IPromotionDetails);
}

export function generatePromotionCode(id: string): Promise<any> {
  return axiosInstance.get(`/virtual_card/?discount=${id}`);
}

export function getActivities(): Promise<IActivity[]> {
  return getNonPaginatedEndpoint(`/activities/`, {}, axiosInstanceV2) as Promise<IActivity[]>;
}

export function getCurrentAddressFromLatLong(latitude: number | string, longitude: number | string): Promise<IAddress> {
  return axios
    .get(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyB5tXrkiapXmzmvkkW0-7MQvfx1LL9VScQ`
    )
    .then(({ data: address }: { data: Record<string, any> }) => {
      const mappedAddress = {
        street: '',
        country: '',
        city: '',
        state: '',
        neighborhood: '',
        cep: '',
        formattedAddress: address.results[0].formatted_address,
      };

      const location: Record<string, string>[] = address.results;

      if (location.length === 0) throw Error('Não foi possível parsear a localização');

      mappedAddress.formattedAddress = location[0].formatted_address;

      return mappedAddress;
    });
}

export function toggleFavourite(promotionId: string, storeId?: string): Promise<any> {
  return axiosInstance.patch(`/profile/${promotionId}/toggle_favorite/`, {
    store_id: storeId,
  });
}

export async function getEconometer(): Promise<IHistory> {
  const { data } = await axiosInstance.get(`/profile/econometer/`);
  data.progress.reverse();
  return data;
}

export async function getSales(params: any): Promise<ISales[]> {
  return axiosInstanceV2
    .get(`/vouchers/my-vouchers`, {
      params,
    })
    .then(({ data }) => data);
}

export function createSale(data: ISale): Promise<any> {
  return axiosInstance.post('/sales/', data);
}

export function getCheckPermission(params: any) {
  return axiosInstance.post(urlWithParams('/profile/check_permission/', params));
}

export const removeAccount = () => {
  return axiosInstance.delete('/profile/delete_account/');
};

export function updateSubscription(id: number, data: any) {
  return axiosInstance.put(`/subscription/my-subscriptions/${id}/`, data);
}

export const sendPartnerEnquiry = (data: any) => {
  return axiosInstance.post('/enquiry/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
